if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        this.wmVariantGalleryEnabled = this.dataset.wmVariantGallery === 'true';
        if (this.wmVariantGalleryEnabled) {
          this.wmProductInfo = this.closest('product-info');
          this.wmOnOptionChange = () => window.setTimeout(() => this.filterWmVariantMedia(), 0);
          this.wmProductInfo?.addEventListener('change', this.wmOnOptionChange);
          if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.variantChange) {
            this.wmVariantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
              if (String(event?.data?.sectionId) !== String(this.dataset.section)) return;
              window.setTimeout(() => this.filterWmVariantMedia(), 0);
            });
          }
          window.requestAnimationFrame(() => this.filterWmVariantMedia());
        }
        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      disconnectedCallback() {
        this.wmProductInfo?.removeEventListener('change', this.wmOnOptionChange);
        this.wmVariantChangeUnsubscriber?.();
      }

      getWmSelectedColor() {
        const optionGroups = this.wmProductInfo?.querySelectorAll('variant-selects [data-option-name]') || [];
        const colorGroup = Array.from(optionGroups).find((group) => {
          const name = (group.dataset.optionName || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
          return ['colore', 'color', 'tonalita'].includes(name);
        });
        if (!colorGroup) return '';
        const selectedInput = colorGroup.querySelector('input[type="radio"]:checked');
        const selectedSelect = colorGroup.querySelector('select');
        return selectedInput?.value || selectedSelect?.value || '';
      }

      normalizeWmColor(value) {
        return String(value || '')
          .normalize('NFD')
          .replace(/[\u0300-\u036f]/g, '')
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, '-')
          .replace(/^-+|-+$/g, '');
      }

      filterWmVariantMedia() {
        if (!this.wmVariantGalleryEnabled) return;
        const selectedColor = this.normalizeWmColor(this.getWmSelectedColor());
        if (!selectedColor) return;

        const viewerItems = Array.from(this.elements.viewer?.querySelectorAll(':scope ul > li[data-media-id]') || []);
        const thumbnailItems = Array.from(this.elements.thumbnails?.querySelectorAll('li[data-target]') || []);
        const matchingViewerItems = viewerItems.filter((item) => item.dataset.wmColor === selectedColor);
        if (!matchingViewerItems.length) return;

        viewerItems.forEach((item) => {
          item.hidden = item.dataset.wmColor !== selectedColor;
        });
        thumbnailItems.forEach((item) => {
          item.hidden = item.dataset.wmColor !== selectedColor;
        });

        const activeMedia = matchingViewerItems.find((item) => item.classList.contains('is-active')) || matchingViewerItems[0];
        if (activeMedia) this.setActiveMedia(activeMedia.dataset.mediaId, false);
        this.elements.viewer?.resetPages?.();
        this.elements.thumbnails?.resetPages?.();
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
